/**
 * NWS Radar Service
 * Fetches radar timestamps from the NWS WMS GetCapabilities endpoint
 */

import type { RadarTimestamp } from "@/utils/radarProvider";

const NWS_CAPABILITIES_URL =
  "https://opengeo.ncep.noaa.gov/geoserver/conus/conus_bref_qcd/ows?service=WMS&version=1.1.1&request=GetCapabilities";

/**
 * Parse ISO timestamps from WMS GetCapabilities XML response.
 * Looks for <Extent name="time"> element containing comma-separated ISO timestamps.
 */
function parseTimestampsFromXml(xml: string): string[] {
  // Match the <Extent name="time" ...> element content
  const extentMatch = xml.match(
    /<Extent[^>]*name="time"[^>]*>([\s\S]*?)<\/Extent>/i,
  );
  if (!extentMatch || !extentMatch[1]) {
    return [];
  }

  const content = extentMatch[1].trim();
  // Timestamps are comma-separated ISO strings
  return content
    .split(",")
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
}

/**
 * Subsample timestamps down to approximately targetCount frames,
 * evenly distributed across the available range.
 */
function subsample(timestamps: string[], targetCount: number): string[] {
  if (timestamps.length <= targetCount) return timestamps;

  const result: string[] = [];
  const step = (timestamps.length - 1) / (targetCount - 1);

  for (let i = 0; i < targetCount; i++) {
    const index = Math.round(i * step);
    result.push(timestamps[index]);
  }

  return result;
}

/**
 * Fetch NWS radar timestamps from GetCapabilities.
 * Returns ~12 actual frames, or none when the service cannot provide them.
 */
export async function fetchNWSRadarTimestamps(): Promise<RadarTimestamp[]> {
  try {
    const response = await fetch(NWS_CAPABILITIES_URL);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const xml = await response.text();
    const allTimestamps = parseTimestampsFromXml(xml);

    if (allTimestamps.length === 0) {
      throw new Error("No timestamps found in GetCapabilities");
    }

    // Subsample to ~12 frames for animation parity with RainViewer
    const sampled = subsample(allTimestamps, 12);

    const result: RadarTimestamp[] = sampled.map((iso) => ({
      source: "nws",
      epoch: Math.floor(new Date(iso).getTime() / 1000),
      isoString: iso,
    }));

    return result;
  } catch (e) {
    console.warn("[NWSRadar] GetCapabilities fetch failed:", e);
    return [];
  }
}
